import React from 'react';
import { Button, type Variant } from './Button';
import { Dropdown } from './Dropdown';

// RowActions — the standardized trailing "Actions" cell for data tables.
//
// Why this exists (see docs/ui-audit.md §8): across 41 tables the same row actions were
// re-implemented with four container idioms, five separate kebab-menu implementations (four
// bypassing the Button primitive), solid-vs-outline variants for identical actions, and nine
// different column widths. Icon-only buttons also shipped without accessible names.
//
// This centralizes all of it:
//   • one icon + variant + accessible label per action kind (ACTION_REGISTRY)
//   • every control is the shared Button, with title + aria-label always set
//   • more than `maxInline` actions collapse into a single kebab menu, which is what makes
//     crowded action columns workable on a phone
//   • pair with ACTIONS_COL_WIDTH on the <th> so the column stops jittering between pages

/** Standard width for an actions <th>. Fits three icon buttons plus gaps. */
export const ACTIONS_COL_WIDTH = '7.5rem';

export type RowActionKind =
  | 'edit' | 'delete' | 'view' | 'history' | 'disable' | 'enable'
  | 'download' | 'attachment' | 'cancel' | 'custom';

interface RegistryEntry {
  icon: string;
  variant: Variant;
  label: string;
  /** Destructive actions should always be confirmed by the caller. */
  destructive?: boolean;
}

/** The single source of truth for row-action iconography. Do not inline `bi-*` in a table. */
export const ACTION_REGISTRY: Record<Exclude<RowActionKind, 'custom'>, RegistryEntry> = {
  edit:       { icon: 'bi-pencil',         variant: 'outline-primary',   label: 'Edit' },
  delete:     { icon: 'bi-trash',          variant: 'outline-danger',    label: 'Delete', destructive: true },
  view:       { icon: 'bi-eye',            variant: 'outline-secondary', label: 'View' },
  history:    { icon: 'bi-clock-history',  variant: 'outline-secondary', label: 'History' },
  disable:    { icon: 'bi-x-circle',       variant: 'outline-danger',    label: 'Disable' },
  enable:     { icon: 'bi-check-circle',   variant: 'outline-success',   label: 'Enable' },
  download:   { icon: 'bi-download',       variant: 'outline-primary',   label: 'Download' },
  attachment: { icon: 'bi-paperclip',      variant: 'outline-primary',   label: 'Attachments' },
  cancel:     { icon: 'bi-x-circle',       variant: 'outline-danger',    label: 'Cancel' },
};

export interface RowAction {
  kind: RowActionKind;
  onClick: () => void;
  /** Overrides the registry label (also used for title + aria-label). */
  label?: string;
  /** Required when kind === 'custom'. */
  icon?: string;
  variant?: Variant;
  hidden?: boolean;
  disabled?: boolean;
  /** Force this action into the overflow menu regardless of count. */
  overflow?: boolean;
}

/** Optional row-expander, rendered before the kebab (the Quantified Tasks pattern). */
export interface RowExpander {
  expanded: boolean;
  onToggle: () => void;
  /** Tooltip/aria-label, e.g. 'View efforts'. Defaults to Expand/Collapse row. */
  title?: string;
}

export interface RowActionsProps {
  actions: RowAction[];
  /**
   * Optional expander chevron shown to the left of the kebab. Use for rows that reveal detail
   * in place (efforts, nested tasks).
   */
  expander?: RowExpander;
  /**
   * How many actions render as standalone buttons before the rest collapse into the kebab.
   * Defaults to 0 — i.e. the app standard is a single three-dot menu, matching Quantified Tasks.
   * Raise it only where a table genuinely needs a one-click action exposed.
   */
  maxInline?: number;
  /** Cell alignment. Default 'right' — the app standard for a trailing actions column. */
  align?: 'left' | 'right';
  className?: string;
}

const resolve = (a: RowAction): RegistryEntry & { label: string } => {
  const base: RegistryEntry = a.kind === 'custom'
    ? { icon: a.icon ?? 'bi-three-dots', variant: 'outline-secondary', label: a.label ?? 'Action' }
    : ACTION_REGISTRY[a.kind];
  return {
    ...base,
    icon: a.icon ?? base.icon,
    variant: a.variant ?? base.variant,
    label: a.label ?? base.label,
  };
};

export const RowActions: React.FC<RowActionsProps> = ({
  actions,
  expander,
  maxInline = 0,
  align = 'right',
  className = '',
}) => {
  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0 && !expander) return null;

  const forced = visible.filter((a) => a.overflow);
  const rest = visible.filter((a) => !a.overflow);
  const inline = rest.slice(0, maxInline);
  // V2-072 / ACT-03: Delete (any destructive action) is always the last menu item, in red.
  const overflow = [...rest.slice(maxInline), ...forced].sort(
    (a, b) => Number(Boolean(resolve(a).destructive)) - Number(Boolean(resolve(b).destructive)),
  );

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-2 ${align === 'right' ? 'tw-justify-end' : ''} ${className}`}
      // Row-level onClick is common on these tables; actions must not trigger navigation.
      onClick={(e) => e.stopPropagation()}
    >
      {/* Expander first, then the kebab — the Quantified Tasks layout, now the app standard. */}
      {expander && (
        <Button
          variant="outline-secondary"
          size="sm"
          className="wrap-touch-target"
          onClick={expander.onToggle}
          active={expander.expanded}
          title={expander.title ?? (expander.expanded ? 'Collapse row' : 'Expand row')}
          aria-label={expander.title ?? (expander.expanded ? 'Collapse row' : 'Expand row')}
          aria-expanded={expander.expanded}
        >
          <i className={`bi bi-chevron-${expander.expanded ? 'up' : 'down'}`} aria-hidden="true" />
        </Button>
      )}

      {inline.map((a, i) => {
        const r = resolve(a);
        return (
          <Button
            key={`${a.kind}-${i}`}
            variant={r.variant}
            size="sm"
            className="wrap-touch-target"
            onClick={a.onClick}
            disabled={a.disabled}
            title={r.label}
            aria-label={r.label}
          >
            <i className={`bi ${r.icon}`} aria-hidden="true" />
          </Button>
        );
      })}

      {overflow.length > 0 && (
        <Dropdown align="end">
          {/* No caret: the kebab is the menu's one control (L6). */}
          <Dropdown.Toggle
            caret={false}
            className="wrap-touch-target tw-border-0 tw-bg-transparent tw-px-2 tw-text-content-secondary hover:tw-bg-surface-hover"
            title="More actions"
            aria-label="More actions"
          >
            <i className="bi bi-three-dots-vertical" aria-hidden="true" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {overflow.map((a, i) => {
              const r = resolve(a);
              return (
                <Dropdown.Item
                  key={`${a.kind}-of-${i}`}
                  onClick={a.onClick}
                  disabled={a.disabled}
                  className={r.destructive ? 'tw-text-danger' : ''}
                >
                  <i className={`bi ${r.icon} tw-mr-2`} aria-hidden="true" />
                  {r.label}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
};

/** Width of the leading expand column (ACT-04), reserved on every row so nothing shifts. */
export const EXPAND_COL_WIDTH = '2.75rem';

export interface RowExpandToggleProps {
  expanded: boolean;
  onToggle: () => void;
  /** Accessible name, e.g. 'Show efforts'. Defaults to Expand / Collapse row. */
  label?: string;
}

/**
 * V2-072 / ACT-04: the row expander, at the LEADING edge of a table row – after the selection
 * checkbox, before the ID / name link – in its own narrow column (EXPAND_COL_WIDTH). Render it
 * only on rows that have details; keep the empty cell on the others. It is never in the Actions
 * column. Card lists keep their chevron on the right of the card header instead.
 */
export const RowExpandToggle: React.FC<RowExpandToggleProps> = ({ expanded, onToggle, label }) => {
  const name = label ?? (expanded ? 'Collapse row' : 'Expand row');
  return (
    <button
      type="button"
      className="wrap-touch-target tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded tw-text-content-secondary hover:tw-bg-surface-hover focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-line-focus"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={name}
      aria-label={name}
      aria-expanded={expanded}
    >
      <i
        className={`bi bi-chevron-right tw-transition-transform tw-duration-150 ${expanded ? 'tw-rotate-90' : ''}`}
        aria-hidden="true"
      />
    </button>
  );
};

export default RowActions;
